import React, { createContext, useContext, useState, useEffect } from 'react';
import { playSound, triggerVibration } from '../lib/soundUtils';

export type CardBackTheme = 'classic' | 'gold' | 'modern';
export type TableTheme = 'green' | 'blue' | 'red';

interface SettingsContextType {
  sfxEnabled: boolean;
  vibrationEnabled: boolean;
  cardBackTheme: CardBackTheme;
  tableTheme: TableTheme;
  setSfxEnabled: (val: boolean) => void;
  setVibrationEnabled: (val: boolean) => void;
  setCardBackTheme: (theme: CardBackTheme) => void;
  setTableTheme: (theme: TableTheme) => void;
  playSfx: (type: 'click' | 'play' | 'scopa' | 'shuffle' | 'deal' | 'error' | 'levelUp' | 'settebello' | 'malus2' | 'dichiarazione' | 'mescola') => void;
  triggerHaptic: (type: 'light' | 'medium' | 'success' | 'warning') => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sfxEnabled, setSfxEnabled] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('sbarazzo_sfx_enabled');
      return saved !== null ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  const [vibrationEnabled, setVibrationEnabled] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('sbarazzo_vibration_enabled');
      return saved !== null ? JSON.parse(saved) : true;
    } catch {
      return true;
    }
  });

  const [cardBackTheme, setCardBackTheme] = useState<CardBackTheme>(() => {
    try {
      const saved = localStorage.getItem('sbarazzo_card_back_theme');
      return (saved as CardBackTheme) || 'classic';
    } catch {
      return 'classic';
    }
  });

  const [tableTheme, setTableTheme] = useState<TableTheme>(() => {
    try {
      const saved = localStorage.getItem('sbarazzo_table_theme');
      return (saved as TableTheme) || 'green';
    } catch {
      return 'green';
    }
  });

  // Persist settings changes
  useEffect(() => {
    try {
      localStorage.setItem('sbarazzo_sfx_enabled', JSON.stringify(sfxEnabled));
    } catch {}
  }, [sfxEnabled]);

  useEffect(() => {
    try {
      localStorage.setItem('sbarazzo_vibration_enabled', JSON.stringify(vibrationEnabled));
    } catch {}
  }, [vibrationEnabled]);

  useEffect(() => {
    try {
      localStorage.setItem('sbarazzo_card_back_theme', cardBackTheme);
    } catch {}
  }, [cardBackTheme]);

  useEffect(() => {
    try {
      localStorage.setItem('sbarazzo_table_theme', tableTheme);
      if (typeof document !== 'undefined') {
        const rootEl = document.documentElement;
        const bodyEl = document.body;
        
        if (rootEl) {
          rootEl.classList.remove('theme-green', 'theme-blue', 'theme-red');
          rootEl.classList.add(`theme-${tableTheme}`);
        }
        
        if (bodyEl) {
          bodyEl.classList.remove('theme-green', 'theme-blue', 'theme-red');
          bodyEl.classList.add(`theme-${tableTheme}`);
        }

        // Dynamically update theme-color meta tag
        const themeMeta = document.querySelector('meta[name="theme-color"]');
        const themeColors = { green: '#1a4d2e', blue: '#1e3a8a', red: '#7f1d1d' };
        if (themeMeta) {
          themeMeta.setAttribute('content', themeColors[tableTheme] || '#1a4d2e');
        }
      }
    } catch (err) {
      console.error('[SettingsContext] Failed to update body/html classes:', err);
    }
  }, [tableTheme]);

  const playSfx = (type: 'click' | 'play' | 'scopa' | 'shuffle' | 'deal' | 'error' | 'levelUp' | 'settebello' | 'malus2' | 'dichiarazione' | 'mescola') => {
    if (sfxEnabled) {
      playSound(type);
    }
  };

  const triggerHaptic = (type: 'light' | 'medium' | 'success' | 'warning') => {
    if (vibrationEnabled) {
      triggerVibration(type);
    }
  };

  return (
    <SettingsContext.Provider
      value={{
        sfxEnabled,
        vibrationEnabled,
        cardBackTheme,
        tableTheme,
        setSfxEnabled,
        setVibrationEnabled,
        setCardBackTheme,
        setTableTheme,
        playSfx,
        triggerHaptic,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};
